import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, query, orderBy, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import './AdminPanel.css';

const AdminPanel = ({ user }) => {
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [activatedPasswords, setActivatedPasswords] = useState({});

  useEffect(() => {
    loadWaitlist();
  }, []);

  const loadWaitlist = async () => {
    try {
      const waitlistRef = collection(db, 'waitlist');
      const q = query(waitlistRef, orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      
      const entries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setWaitlistEntries(entries);
    } catch (error) {
      console.error('Error loading waitlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const activateAccount = async (entry) => {
    setProcessing(prev => ({ ...prev, [entry.id]: true }));
    
    try {
      // Generate a random UID for the user (Firebase format)
      const generateUID = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let uid = '';
        for (let i = 0; i < 28; i++) {
          uid += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return uid;
      };
      
      const userUID = generateUID();
      
      // Create user document in Firestore with proper structure
      await setDoc(doc(db, 'users', userUID), {
        email: entry.email,
        isSubscribed: true,
        subscriptionType: 'free',
        subscriptionStartDate: new Date(),
        maxAccounts: 1,
        maxVideosPerMonth: 10,
        videosUsedThisMonth: 0,
        currentPeriodStart: new Date(),
        twitterUsernames: [],
        activatedFromWaitlist: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        isUnlimited: false,
        totalVideos: 0,
        recentImports: []
      });
      
      // Update waitlist entry status
      await updateDoc(doc(db, 'waitlist', entry.id), {
        status: 'activated',
        activatedAt: new Date(),
        activatedBy: user.uid,
        preApprovedUID: userUID
      });
      
      // Refresh waitlist
      await loadWaitlist();
      
      console.log(`Account approved: ${entry.email} with UID: ${userUID}`);
      alert(`Account approved successfully!\n\nEmail: ${entry.email}\nUser ID: ${userUID}\n\nThe user can now:\n1. Sign in with Google (if using the same email)\n2. Go to /signup to create a password\n\nUser document created - they'll be recognized as approved when they sign up.`);
      
    } catch (error) {
      console.error('Error activating account:', error);
      alert(`Error activating account: ${error.message}`);
    } finally {
      setProcessing(prev => ({ ...prev, [entry.id]: false }));
    }
  };

  const copyPassword = (password) => {
    navigator.clipboard.writeText(password);
    alert('Password copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="admin-header">
          <h1>🔧 Admin Panel</h1>
        </div>
        <div className="loading-spinner-admin">Loading waitlist...</div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>🔧 Admin Panel</h1>
        <p>Manage waitlist and activate accounts</p>
      </div>

      <div className="admin-stats">
        <div className="stat-card">
          <h3>{waitlistEntries.length}</h3>
          <p>Total Signups</p>
        </div>
        <div className="stat-card">
          <h3>{waitlistEntries.filter(e => e.status === 'activated').length}</h3>
          <p>Activated</p>
        </div>
        <div className="stat-card">
          <h3>{waitlistEntries.filter(e => e.status === 'pending').length}</h3>
          <p>Pending</p>
        </div>
      </div>

      <div className="waitlist-table">
        <h2>Waitlist Entries</h2>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Date Joined</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {waitlistEntries.map(entry => (
                <tr key={entry.id}>
                  <td>{entry.email}</td>
                  <td>{entry.timestamp?.toDate().toLocaleDateString()}</td>
                  <td>
                    <span className={`status-badge ${entry.status || 'pending'}`}>
                      {entry.status === 'activated' ? '✅ Activated' : '⏳ Pending'}
                    </span>
                  </td>
                  <td>
                    {entry.status === 'activated' ? (
                      <div className="activated-info">
                        {activatedPasswords[entry.id] && (
                          <div className="password-display">
                            <code>{activatedPasswords[entry.id]}</code>
                            <button 
                              onClick={() => copyPassword(activatedPasswords[entry.id])}
                              className="copy-btn"
                            >
                              📋
                            </button>
                          </div>
                        )}
                        <span className="activated-text">Account Created</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => activateAccount(entry)}
                        disabled={processing[entry.id]}
                        className="activate-btn"
                      >
                        {processing[entry.id] ? '⏳ Creating...' : '🚀 Activate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel; 